import React, { useCallback, useMemo, useEffect, useState } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';

// GLOBAL FUNCTIONS
import { lookupActions } from 'actions.js';
import { apiFetch, makePath, getFileType } from 'functions.js';

// GLOBAL HELPERS
import useRecordView from 'helpers/getRecordView';

// LOCAL COMPONENTS
import Card from 'parts/card/Card';
import Form from 'core/form/Form';
import { compose } from 'redux';
import { connect } from 'react-redux';

// LOCAL COMPONENTS
import ToggledTable from '../tools/ToggledTable';
import UploadItem from 'routers/interface/tools/UploadItem';

// STYLES
import styles from './rebates.module.scss';

// VARIABLES
const OPTION_KEYS = {
  label: 'rebate_title',
  value: 'dealer_rebate_id'
}
const ITEM_CONSTRUCTOR = obj => ({
  sales_rebate_id: '',
  dealer_rebate_id: obj?.dealer_rebate_id || '',
  item_Amount: obj?.rebate_amount,
  item_name: obj?.rebate_title,
  item_code: obj?.rebate_program_code || '',
  is_admin_rebate: !!obj,
  item_add_as_task: false,
  item_Amount_lock: !!obj?.rebate_amount_lock,
  aryRebateAttachment: obj?.rebate_attachments || []
});

// FORM COMPONENT
const RebatesForm = compose(
  connect(
    null,
    {...lookupActions}
  )
)(({
  // REACT DISPATCH
  load
}) => {

  // CONTEXT
  const { isAdd } = useRecordView();
  const { values, setFieldValue } = useFormikContext() || {};
  const [options, setOptions] = useState([]);

  // MEMOS
  const {
    daily_sales_inventory_id
  } = useMemo(
    () => values || {},
    [values]
  )

  // CALLBACKS
  const addAutoRebates = useCallback(
    autoRebates => {
      if (!autoRebates?.length) return;
      setFieldValue('has_rebate', true);
      const value = autoRebates.map(ITEM_CONSTRUCTOR);
      setFieldValue('aryRebate', value);
    },
    [setFieldValue]
  )

  // MEMOS
  useEffect(
    () =>
      apiFetch({
        endpoint: makePath(ENDPOINTS.sales.rebate, daily_sales_inventory_id),
        onSuccess: data => {
          setOptions(
            data.dealerRebates.map(({ [OPTION_KEYS.label]: label, [OPTION_KEYS.value]: value }) => ({
              label,
              value
            }))
          );
          if (isAdd) addAutoRebates(data.autoRebates);
        }
      }),
    [daily_sales_inventory_id, isAdd, addAutoRebates]
  );

  const lookupArgs = useMemo(
    () => ({
      endpoint: makePath(ENDPOINTS.sales.rebate, daily_sales_inventory_id),
      onSuccess: data => {
        load('dealerRebates', data.dealerRebates);
      }
    }),
    [daily_sales_inventory_id, load]
  )

  const handleChange  = useCallback((item,i) => {
    if(!isAdd && item.sales_rebate_id !== '' ) {
      setFieldValue(`aryRebate[${i}].isUpdated`,true)
    }
   },[setFieldValue, isAdd])

   const handleDelete = useCallback((item,i) => {
    if(!isAdd && item.sales_rebate_id !== '' ) {
      setFieldValue(`aryRebate[${i}].isUpdated`, false);
      setFieldValue(`aryRebate[${i}].isDeleted`,true)
      return true;
    }
    else return false;
    
   },[setFieldValue, isAdd])

  // RENDER
  return (
    <Form.Body>
      <Form.Col>
        <ToggledTable
          name="aryRebate"
          flagName="has_rebate"
          label="Rebate To Add?"
          itemLabel="Rebate"
          lookup="dealerRebates"
          lookupArgs={lookupArgs}
          optionKeys={OPTION_KEYS}
          itemConstructor={ITEM_CONSTRUCTOR}
          useCustom
          options={options}
          buttonBefore
          handleDelete={(item,i) => handleDelete(item,i)}
        >{(item = {}, i) => [
          <Form.Control
            name="item_Amount"
            label="Amount"
            schema="dollars"
            plaintext={item.is_admin_rebate && item.item_Amount_lock}
            onChange={() => handleChange(item,i)}
            colWidth={6}
          />,
          <Form.Control
            name="item_name"
            label="Description"
            maxLength={100}
            plaintext={item.is_admin_rebate}
            onChange={() => handleChange(item,i)}
            colWidth={10}
            required
          />,
          <Form.Control
            name="item_code"
            label="Code"
            onChange={() => handleChange(item,i)}
            plaintext={item.is_admin_rebate}
            colWidth={4}
          />,
          <Form.YesNo
            name="item_add_as_task"
            text="Add as Task"
            type="checkbox"
            onChange={() => handleChange(item,i)}
            numeric={false}
            colWidth={4}
          />,
          ...(item?.aryRebateAttachment?.map((download, d) => {
            const endpoint = isAdd
              ? makePath(ENDPOINTS.uploads.rebate.view(item.dealer_rebate_id), download.rebateattachment_id)
              : makePath(ENDPOINTS.uploads.sales.viewRebate, download.rebateattachment_id);
            const mimeType = getFileType(download.rebateattachment_filename) || 'text/plain'
            return (
              <Form.Col className="offset-lg-6 pt-0" colWidth={18}>
                <UploadItem
                  {...download}
                  className="py-0"
                  meta={{
                    fileName: download.rebateattachment_filename,
                    fileId: download.rebateattachment_id,
                    uploadedBy: download.updated_by || download.created_by || download.uploadedBy,
                    uploadedOn: download.date_updated || download.date_created || download.uploadedOn
                  }}
                  viewArgs={{
                    endpoint,
                    contentType: mimeType,
                    mimeType
                  }}
                  allowDelete={false}
                  key={`${download.dp_attachment_id}-${d}`}
                  containerClassName={styles.attachment}
                />
              </Form.Col>
            );
          }) || [])
        ]}</ToggledTable>
      </Form.Col>
    </Form.Body>
  )
})

export default function Rebates(props) {
  return <Card title="Rebate(s)" body={<RebatesForm {...props} />} />;
}
